import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { api } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";
import { getNextPaginatePage } from "@/utils";

import { type ReviewFeedback } from "../types";

export type ReviewFeedbackList = Paginate<ReviewFeedback<false, true, true>>;

export const reviewFeedback = createQueryKeys("performance-review-feedbacks", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],

  period: (periodId?: number) => ({
    queryKey: [periodId],
    contextQueries: {
      feedbacks: (page?: number, filter?: SearchRequest) => [{ page, filter }],
      feedbacksInfinite: (filter?: SearchRequest) => [{ filter }],
    },
  }),

  review: (id?: number) => [id],

  one: (id?: number) => [id],
});

const include = "review.user.position.unit,reviewer";

export const fetchPendingReviewFeedbacksQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...reviewFeedback.all(page, search),
    queryFn: () =>
      api
        .get<ReviewFeedbackList>(`/performance/feedback`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchPendingReviewFeedbacks = (search?: SearchRequest) =>
  usePaginateQuery((page) =>
    fetchPendingReviewFeedbacksQueryOptions(page, search),
  );

export const fetchPeriodReviewFeedbacksQueryOptions = (
  periodId: number,
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...reviewFeedback.period(periodId)._ctx.feedbacks(page, search),
    queryFn: () =>
      api
        .get<ReviewFeedbackList>(
          `/performance/review-periods/${periodId}/feedback`,
          {
            params: { page, include, ...search },
          },
        )
        .then((response) => response.data),
    enabled: periodId !== undefined,
  });

export const useFetchPeriodReviewFeedbacks = (
  periodId: number,
  search?: SearchRequest,
) =>
  usePaginateQuery((page) =>
    fetchPeriodReviewFeedbacksQueryOptions(periodId, page, search),
  );

export const fetchInfinitePeriodReviewFeedbacksQueryOptions = (
  periodId: number,
  search?: SearchRequest,
) =>
  infiniteQueryOptions({
    ...reviewFeedback.period(periodId)._ctx.feedbacksInfinite(search),
    queryFn: ({ pageParam }) =>
      api
        .get<ReviewFeedbackList>(
          `/performance/review-periods/${periodId}/feedback`,
          {
            params: { page: pageParam, include, ...search },
          },
        )
        .then((response) => response.data),
    enabled: periodId !== undefined,
    initialPageParam: 1,
    getNextPageParam: getNextPaginatePage,
  });

export const useFetchInfinitePeriodReviewFeedbacks = (
  periodId: number,
  search?: SearchRequest,
) =>
  useInfiniteQuery({
    ...fetchInfinitePeriodReviewFeedbacksQueryOptions(periodId, search),
  });

export const fetchReviewReviewFeedbacksQueryOptions = (reviewId?: number) =>
  queryOptions({
    ...reviewFeedback.review(reviewId),
    queryFn: () =>
      api
        .get<ReviewFeedback<true, true, false, true, true, true>[]>(
          `/performance/reviews/${reviewId}/feedback`,
          {
            params: { include: "reviewPeriodType,objectiveRatings,objectives" },
          },
        )
        .then((response) => response.data),
    enabled: reviewId !== undefined,
  });

export const fetchReviewFeedbackQueryOptions = (feedbackId?: number) =>
  queryOptions({
    ...reviewFeedback.one(feedbackId),
    queryFn: () =>
      api
        .get<ReviewFeedback<true, true, false, true, true, true>>(
          `/performance/feedback/${feedbackId}`,
          {
            params: { include: "objectiveRatings" },
          },
        )
        .then((response) => response.data),
    enabled: feedbackId !== undefined,
  });

export const useFetchReviewFeedback = (feedbackId?: number) =>
  useQuery({
    ...fetchReviewFeedbackQueryOptions(feedbackId),
  });

export const useFetchReviewReviewFeedbacks = (reviewId?: number) =>
  useQuery({
    ...fetchReviewReviewFeedbacksQueryOptions(reviewId),
  });

export interface ObjectiveRatingRequest {
  performance_objective_id: number;
  rating: string | null;
  summary: string | null;
  feedback: string | null;
}

export interface ReviewFeedbackRequest {
  rating: string | null;
  comment: string | null;
  objectives?: ObjectiveRatingRequest[];
  elements: {
    id: number;
    value: any;
  }[];
}

interface ValidateReviewFeedbackResponse {
  success: boolean;
}

export const useValidateReviewFeedback = (id: number) =>
  useMutationWithError({
    mutationFn: (data: ReviewFeedbackRequest) =>
      api
        .post<ValidateReviewFeedbackResponse>(
          `/performance/feedback/${id}/validate`,
          data,
        )
        .then((response) => response.data),
  });

export const useUpdateReviewFeedback = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: ReviewFeedbackRequest) =>
      api
        .put<ReviewFeedback>(`/performance/feedback/${id}`, data)
        .then((response) => response.data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: reviewFeedback.all._def,
      });
      queryClient.invalidateQueries(
        reviewFeedback.review(res.performance_review_id),
      );
      queryClient.invalidateQueries(reviewFeedback.one(id));
    },
  });
};
